//import { sfuUrlBase } from './fetch.js';
import { MakedoError, ErrorCodes, fromMediaError } from './errors.js';
import VideoPipeline from './video-pipeline.js';

/* import PixelizeVideoProcessor from './PixelizeVideoProcessor.js'; */
/* Makedo basic client example. */

// Reserved stream labels with built-in capture recipes (getConfigByType) and
// dedicated named APIs (toggleVideo/toggleScreenshare, etc.). These sit ABOVE
// the generic labeled-stream machinery: they are just well-known labels whose
// behavior is frozen for backward compatibility. Any other (custom) label is
// published by the app via publishStream() with an app-supplied MediaStream or
// constraints, and routes through the exact same upstream path.
const RESERVED_LABELS = ["camera", "microphone", "movie", "screenshare"];

class Streams {

    // channel {sfu_url, repOnServer, joinAsUsername, joinWithPassword, channelId}
    // sfu_url // URL from channel data
    // repOnServer // {name, authPortal, endpoint, location}
    // others // "example-user", "example-password", "C8732987432894798274"

    constructor(bridge) {
        this.bridge = bridge; // Passed bridge reference
        // Live REMOTE camera audio tracks, read by getAllCameraAudioTracks() to seed the
        // transcription mix. A Set, not an array: re-registering the same track is a no-op.
        // Entries leave on three paths — the track ends, its downstream closes, or the SFU
        // connection is rebuilt — and the getter drops any non-live straggler on read, so a
        // missed teardown cannot leak a dead track into the recorder or grow this unboundedly.
        this.downstreamAudioTracks = new Set();

        //this.defaultServerRefGlobal = sfuUrlBase; 
        this.sfuConnection = null;
        
        // Stream management:
        // sourceStreams: Original MediaStreams from getUserMedia() / getDisplayMedia() — hardware owners.
        //   These exist independently of broadcasting (e.g. for local preview).
        // activeUpstreams: Everything related to an active broadcast, keyed by mediaSourceType.
        //   { gStream, clonedStream, audioTrack, videoTrack }
        //   gStream     — Galene protocol stream handle
        //   clonedStream — MediaStream clone sent to the peer connection (not the source)
        //   audioTrack  — cloned audio track currently in the peer connection (or null)
        //   videoTrack  — cloned video track currently in the peer connection (or null)
        this.sourceStreams = {};    // key=label (reserved type OR custom), value=MediaStream (hardware/source owner)
        this.activeUpstreams = {}; // key=label, value={gStream, clonedStream, audioTrack, videoTrack}
        // Per-custom-label encoding overrides supplied by the app via publishStream({encodings}).
        // key=label, value={ video:RTCRtpEncodingParameters[], audio:RTCRtpEncodingParameters[] }.
        // Reserved labels are NOT stored here — they keep getEncodingsByTypeKind's built-in defaults.
        this.customEncodings = {};
        // Derived (tapped) video. A VideoPipeline reads one source label's video track and fans
        // it out to N downscaled taps, each published under its own label via
        // publishStream({from}). One read, N outputs — a thumbnail costs a blit, not a capture.
        //   videoPipelines: key=SOURCE label ('camera'), value=VideoPipeline
        //   videoTaps:      key=TAP label ('thumbnail'), value=source label — the reverse index
        //                   teardown needs, since teardown arrives by published label.
        this.videoPipelines = {};
        this.videoTaps = {};
        this.videoPipelineStats = {}; // key=source label, value=last {sourceFps, ms, driver, outputs}
        // Default downstream subscription map applied on join/change. Overridable via
        // setDefaultSubscription(); the '' key is the wildcard applied to any unlisted label.
        this.defaultRequest = {'': ['audio', 'video']};
        this.currentCameraAudioDeviceId = null;
        this.currentCameraVideoDeviceId = null;
    
        this.currentChannel = {};    //{sfu_url, repOnServer, joinAsUsername, joinWithPassword, channelId};
        this.weInitiatedLeave = false;  // Track if client initiated disconnect vs server kicked
    
        // ================================  SERVER CALLBACKS  ==================================
        // USED
        this.onSystemMessageToUserExt = (() => {console.debug("STREAMS EVENT: onSystemMessageToUser called but no callback set.")});
        this.onSelfJoinedExt = (() => {console.debug("STREAMS EVENT: onSelfJoined called but no callback set.")});
        this.onSelfExitedExt = (() => {console.debug("STREAMS EVENT: onSelfExited called but no callback set.")});
        this.onNewDownStreamExt = (() => {console.debug("STREAMS EVENT: onNewDownStream called but no callback set.")});
        this.onNewUpStreamExt = (() => {console.debug("STREAMS EVENT: onNewUpStream called but no callback set.")});
        this.onEndDownStreamExt = (() => {console.debug("STREAMS EVENT: onEndDownStream called but no callback set.")});
        this.onEndDownTrackExt = (() => {console.debug("STREAMS EVENT: onEndDownTrack called but no callback set.")});
        this.onChatMessageExt = (() => {console.debug("STREAMS EVENT: onChatMessage called but no callback set.")});
    
        // UNUSED
        this.onNewMemberExt = (() => {console.debug("STREAMS EVENT: onNewMember no callback set.")});
        this.onChangedMemberStatusExt = (() => {console.debug("STREAMS EVENT: onChangedMemberStatus called but no callback set.")});
        this.onChangedDownAudioStateExt = (() => {console.debug("STREAMS EVENT: onChangedDownAudioState called but no callback set.")});
        this.onChannelMessageToMemberExt = (() => {console.debug("STREAMS EVENT: onChannelMessageToMember called but no callback set.")});
        this.onNewChannelExt = (() => {console.debug("STREAMS EVENT: onNewChannel called but no callback set.")});
        this.onChangedChannelStatusExt = (() => {console.debug("STREAMS EVENT: onChangedChannelStatus called but no callback set.")});
    }
    

    // system state callback functions that can be defined
    setOnSystemMessageToUser(callback) {this.onSystemMessageToUserExt=callback;} // callback receives (channelId, messageJson)
     setOnSelfJoined(callback) {this.onSelfJoinedExt=callback; } // callback receives (channelId, channelJson)
     setOnSelfExited(callback) {this.onSelfExitedExt=callback;} // callback receives (channelId, channelJson)
     setOnNewDownstream(callback) {this.onNewDownStreamExt=callback;} // callback receives (json:{channelId, memberId, userId, streamId, type, streamObj})
     setOnNewUpstream(callback) {this.onNewUpStreamExt=callback;} // callback receives (json:{channelId, memberId, userId, streamId, type, streamObj})
     setOnEndDownstream(callback) {this.onEndDownStreamExt=callback;} // callback receives (json:{channelId, memberId, type, streamId})
     setOnEndDownTrack(callback) {this.onEndDownTrackExt=callback;} // callback receives (json:{channelId, memberId, streamType, trackType} )
     setOnChatMessage(callback) {this.onChatMessageExt=callback;} // callback receives (channelId, chatJson)
    // UNUSED
     setOnNewMember(callback) {this.onNewMemberExt=callback;} // callback receives (json:{channelId, memberJson})
     setOnChangedMemberStatus(callback) {this.onChangedMemberStatusExt=callback;} // callback receives (channelId, memberId, newStatus)
     setOnChangedDownAudioState(callback) {this.onChangedDownAudioStateExt=callback;} // callback receives (json:{channelId, memberId, type, streamId, newState})
     setOnChannelMessageToMember(callback) {this.onChannelMessageToMemberExt=callback;} // callback receives (channelId, memberId, messageJson)
     setOnNewChannel(callback) {this.onNewChannelExt=callback;}  // callback receives (channelId, channelJson)
     setOnChangedChannelStatus(callback) {this.onChangedChannelStatusExt=callback;} // callback receives (channelId, newStatus)
    // ================================  SERVER CALLBACKS  ==================================

    async loginToCurrentChannel() { // PRIVATE function
        let targetChannel=this.currentChannel;
        
        // fetch the channel information {name, authPortal, endpoint, location}
        //let allGaleneChannelIdsGlobal=await fetch(galeneProtocDomain+"/public-groups.json");
        let url=targetChannel.sfu_url+"/group/"+targetChannel.channelId+"/";
        let r = await fetch(url + ".status");
        if(!r.ok) { throw new MakedoError(ErrorCodes.ROOM_SFU_UNREACHABLE, `Video server returned ${r.status} ${r.statusText} for ${url}`, { context: 'loginToCurrentChannel', retriable: true }); }
        let galeneGroupStatusInfo = await r.json();

        targetChannel.repOnServer=galeneGroupStatusInfo;


        // parse a token in the URL.
        let token = null;
        let parms = new URLSearchParams(window.location.search);
        if(parms.has('token')) {token = parms.get('token');}

        // connect to the server
        if(token) {this.serverConnect(targetChannel.repOnServer, targetChannel.joinAsUsername, targetChannel.joinWithPassword, token);} 
        else if(targetChannel.repOnServer.authPortal) { window.location.href = targetChannel.repOnServer.authPortal; return; } 
        else { 
            this.serverConnect(targetChannel.repOnServer, targetChannel.joinAsUsername, targetChannel.joinWithPassword, null); 
        }
    }

     async serverConnect(channelRepOnServer, username, attemptedPassword, token) { // PRIVATE function, clled by loginToCurrentChannel()
        let channelIdFromGalene=channelRepOnServer.name;  // Galene calls these group name: (groupStatus.name~channelData.channelRepOnServer.channelId)
        if (!username) {
            console.warn('[Streams] serverConnect called without username — cannot join SFU');
            return;
        }
        
        // Wait for protocol.js to be loaded before creating ServerConnection
        await this.bridge.protocolReady;
        
        // Check if we already have a connection - CLOSE IT to prevent multiple connections
        if (this.sfuConnection) {
            this.sfuConnection.close();
            this.sfuConnection = null;
        }
        // A rebuilt connection re-delivers every downstream from scratch, so anything still
        // registered belongs to the dead connection. Backstop for the per-stream cleanup below.
        this.downstreamAudioTracks.clear();

        // create the connection to the server
        const streams = this; // Capture Streams instance for use in conn callbacks below
        let conn = new ServerConnection();
        conn.onconnected = async function() {
            streams.bridgeDisplayStatus('Connected');
            let creds = token ?
                {type: 'token', token: token} :
                {type: 'password', password: attemptedPassword};
            // join the channel and wait for the onjoined callback
            await this.join(channelIdFromGalene, username, creds);
        };
        conn.onchat = (id, dest, username, time, privileged, history, kind, message) => {
            streams.onChatMessageExt(id, dest, username, time, privileged, history, kind, message);
        };
        conn.onusermessage = (id, dest, username, time, privileged, kind, error, message) => {
            streams.onSystemMessageToUserExt(id, dest, username, time, privileged, kind, error, message);
        };
        conn.ondownstream = (gStream) => {
            // gStream contains {gStream.localId, gStream.label, gStream.stream}
            // The camera audio tracks THIS downstream contributed to downstreamAudioTracks.
            // Kept in the closure rather than looked up off gStream at close time: protocol.js
            // has already unwound the stream by the time onclose fires, and a Galene stream
            // REPLACE closes the old gStream while a new one carries the new track — so
            // unregistering exactly what this gStream registered is the only safe rule.
            const myAudioTracks = new Set();
            gStream.onclose = (replace) => {
                for (const t of myAudioTracks) { streams.downstreamAudioTracks.delete(t); }
                myAudioTracks.clear();
                streams.onEndDownStreamExt(gStream.localId, gStream.username, gStream.label);
            };
            gStream.ondowntrack = (track, transceiver, newStream) => {
                if (track && !track.onended) {
                    track.onended = () => {
                        console.debug("DOWN track ended: "+gStream.username+","+gStream.label+","+track.kind);
                        // Harmless no-op unless this was a registered camera audio track.
                        streams.downstreamAudioTracks.delete(track);
                        myAudioTracks.delete(track);
                    };
                }
                if (!(gStream.label==="screenshare")) { // screenshare system-muting is too frequent to log
                    if (!track.onmute) {
                        track.onmute = () => {
                            console.debug(`${gStream.label} DOWN-TRACK AWAY: Remote ${track.kind} went away`);
                            streams.onEndDownTrackExt(gStream.localId, gStream.username, gStream.label, track.kind);
                        };
                        track.onunmute = () => { console.debug(`${gStream.label} DOWN-TRACK BACK: Remote ${track.kind} got back`); };
                    }
                }
                if (gStream.label==="camera" && track.kind==="audio") {
                    streams.downstreamAudioTracks.add(track);
                    myAudioTracks.add(track);
                }
                streams.onNewDownStreamExt(gStream.localId, gStream.username, gStream.label, newStream, false);
            };
            streams.onNewDownStreamExt(gStream.localId, gStream.username, gStream.label, gStream.stream, true);
        };
        conn.onjoined = async function(kind, channel, perms, status, data, error, message) {
            switch(kind) {
            case 'fail':
                streams.bridgeDisplayError(message);
                this.close();
                streams.onSelfExitedExt();
                streams.bridgeSetMemberStatusDb(this.username, "steppedout", "member_left");
                break;
            case 'redirect':
                this.close();
                document.location.href = message;
                return;
            case 'leave':
                streams.bridgeDisplayStatus('Connected (?) Leave');
                if (!streams.weInitiatedLeave) {
                    // Server kicked us or forced disconnect - go to lobby
                    streams.bridgeSetMemberStatusDb(this.username, "lobby_mid", "member_left");
                    console.debug("Server initiated leave - status set to lobby_mid");
                } else {
                    console.debug("Client initiated leave - status already set");
                }
                streams.weInitiatedLeave = false;  // Reset flag
                this.close();
                streams.onSelfExitedExt();
                break;
            case 'join':
                streams.bridgeDisplayStatus(`You are now connected as [${this.username}] at [${this.group}].`);  // protocol.js uses "group" for channel name
                // request downstreams per the current default subscription map (overridable via setDefaultSubscription)
                this.request(streams.defaultRequest);
                streams.onSelfJoinedExt(); // callback receives (channelId, channelJson)
                streams.bridgeSetMemberStatusDb(this.username, "nowinside", "member_joined");
                break;
            case 'change':
                // Group parameters changed (e.g. Galene sent kickall+lock which broadcasts a 'change'
                // event to all connected clients before sending 'leave' and closing.
                // The member is already inside — do NOT re-signal nowinside or re-run onSelfJoined.
                // Just re-request streams in case the change affected media permissions.
                this.request(streams.defaultRequest);
                break;
            default:
                streams.bridgeDisplayError(`Unexpected state ${kind}.`);
                this.close();
                break;
            }
        };
        conn.onclose = () => { streams.bridgeDisplayStatus('Disconnected'); };
        // Now connect and wait for the onconnected callback
        conn.connect(channelRepOnServer.endpoint);

        //sfuConnectionGlobal=conn;
        this.sfuConnection=conn;
    }

    getEncodingsByTypeKind(type, kind) {
        // Custom published labels carry app-supplied encodings (scoped to custom labels only —
        // reserved camera/screenshare tuning below is never touched). Falls through to defaults
        // for any kind the app did not specify.
        const custom = this.customEncodings[type];
        if (custom) {
            if (kind === "video" && custom.video) { return custom.video; }
            if (kind === "audio" && custom.audio) { return custom.audio; }
        }
        let encodings=[];
        if (kind==="audio") {
            if (type==="camera") {encodings=[{maxBitrate: 128000}];}
            if (type==="screenshare") {encodings=[{maxBitrate: 128000}];}
        }
        if (kind==="video") {
            if (type==="camera") {encodings=[{maxBitrate: 4000000, priority: 'high', networkPriority: 'high'}];}
            if (type==="screenshare") {encodings=[{maxBitrate: 1000000000}];} // uncapped — screenshare needs high bitrate for sharp text/UI
        }
        return encodings;
    }

    preferVP9(transceiver) {
        try {
            const capabilities = RTCRtpSender.getCapabilities('video');
            if (capabilities) {
                const vp9   = capabilities.codecs.filter(c => c.mimeType.toLowerCase() === 'video/vp9');
                const other = capabilities.codecs.filter(c => c.mimeType.toLowerCase() !== 'video/vp9');
                transceiver.setCodecPreferences([...vp9, ...other]);
            }
        } catch(e) {
            console.warn('setCodecPreferences not supported:', e);
        }
    }


    getConfigByType(type, audioOn, videoOn, deviceId) {
        let config=null;
        switch(type) {
            case "camera":
                config={audio:true, video: {width: { ideal: 1920 },height: { ideal: 1080 }, frameRate: {ideal: 30}}};
                if (deviceId && deviceId !== "default") {config.video.deviceId = {exact: deviceId};} // use the specific camera device
                break;
            case "microphone":
                config={audio:{echoCancellation: true,noiseSuppression: true,sampleRate: 44100}, video: false};
                if (deviceId && deviceId !== "default") {config.audio.deviceId = {exact: deviceId};}
                break;
            case "movie":
                config={ audio: true, video:  true };  // TBD - movie video config
                break;
            case "screenshare":    
                config = {
                    video: { cursor: "always" }, // no resolution or framerate constraints — capture at native resolution and browser-default fps
                    audio: {echoCancellation: false, noiseSuppression: false, autoGainControl: false},
                    surfaceSwitching: "include", selfBrowserSurface: "include", systemAudio: "include" 
                };
                break;
        }
        if (type==null && deviceId!=null) {config={ audio: { deviceId: deviceId }, video: { deviceId: deviceId }};}

        if (!audioOn) {config.audio=false;} // only video
        if (!videoOn) {config.video=false;} // only audio
        return config;
    }
    async createMediaStreamByTypeAndConfig(type, config) { 
        if (!type || !config) {return null;}
        let stream;
        if (type=='screenshare') {
            if (!('getDisplayMedia' in navigator.mediaDevices)) {
                throw new MakedoError(ErrorCodes.SCREENSHARE_UNSUPPORTED, 'Your browser does not support screen sharing', { context: 'createMediaStreamByTypeAndConfig', retriable: false });
            }
            stream = await navigator.mediaDevices.getDisplayMedia(config);
            const screenVideoTrack = stream.getVideoTracks()[0];
            if (screenVideoTrack && 'contentHint' in screenVideoTrack) {
                screenVideoTrack.contentHint = 'detail'; // 'detail' preserves spatial resolution — correct for screen content
            }
        } else {
            stream = await navigator.mediaDevices.getUserMedia(config);
            if (type === 'camera') {
                const cameraVideoTrack = stream.getVideoTracks()[0];
                if (cameraVideoTrack && 'contentHint' in cameraVideoTrack) {
                    cameraVideoTrack.contentHint = 'motion'; // prioritize smooth motion over spatial detail
                }
            }
        }
        return stream;
    }


    // FUNCTIONS FOR MANAGING LOCAL STREAMS
    // Every source-stream assignment funnels through here, which is why it is the single place
    // a derived-video pipeline gets re-pointed at its new input (camera restart, device switch).
    local_setStream(type, existingStream) {if (type && existingStream) {this.sourceStreams[type]=existingStream; this.retargetVideoPipeline(type);}}
    async startMedia(type, audioOn, videoOn, deviceId) { //→ Promise<MediaStream>
        // Check if existing stream has the requested tracks AND they're still alive
        let existingStream = this.sourceStreams[type];
        if (existingStream) {
            // Check track states
            let audioTracks = existingStream.getAudioTracks();
            let videoTracks = existingStream.getVideoTracks();
            let hasLiveAudio = audioTracks.length > 0 && audioTracks[0].readyState === 'live';
            let hasLiveVideo = videoTracks.length > 0 && videoTracks[0].readyState === 'live';
            let hasEnabledAudio = hasLiveAudio && audioTracks[0].enabled;
            let hasEnabledVideo = hasLiveVideo && videoTracks[0].enabled;
            // Determine what needs to be done for each track type
            let audioNeedsRecreate = audioOn && audioTracks.length > 0 && !hasLiveAudio; // Stopped/ended
            let audioNeedsEnable = audioOn && hasLiveAudio && !hasEnabledAudio; // Just disabled
            let audioNeedsAdd = audioOn && audioTracks.length === 0; // Missing entirely
            let videoNeedsRecreate = videoOn && videoTracks.length > 0 && !hasLiveVideo; // Stopped/ended
            let videoNeedsEnable = videoOn && hasLiveVideo && !hasEnabledVideo; // Just disabled
            let videoNeedsAdd = videoOn && videoTracks.length === 0; // Missing entirely
            // Handle each track type independently
            if (audioNeedsRecreate) {
                await this.local_replaceAudioTrack(type, deviceId);
                existingStream = this.sourceStreams[type]; // Get updated stream
            } else if (audioNeedsEnable) {
                audioTracks[0].enabled = true;
                this.syncAudioToClone(type, true);
            } else if (audioNeedsAdd) {
                await this.local_addAudioTrack(type, deviceId);
                existingStream = this.sourceStreams[type];
            }
            
            if (videoNeedsRecreate) {
                await this.local_replaceVideoTrack(type, deviceId);
                existingStream = this.sourceStreams[type]; // Get updated stream
            } else if (videoNeedsEnable) {
                videoTracks[0].enabled = true;
                this.syncVideoToClone(type, true);
            } else if (videoNeedsAdd) {
                await this.local_addVideoTrack(type, deviceId);
                existingStream = this.sourceStreams[type];
            }
            
            // Check if we now have what we need
            let currentAudio = existingStream.getAudioTracks().length > 0;
            let currentVideo = existingStream.getVideoTracks().length > 0;
            let hasWhatWeNeed = (!audioOn || currentAudio) && (!videoOn || currentVideo);
            
            if (hasWhatWeNeed) {
                return existingStream;
            }
            
            // If selective operations failed, fall through to full recreation
        }
        
        // No existing stream or needs full recreation
        let newStream=null;
        let addMirroring=false;
        let config=null;
        switch(type) {
            case "camera":
                addMirroring=true;
                config =this.getConfigByType(type, audioOn, videoOn, deviceId);
                newStream = await this.createMediaStreamByTypeAndConfig(type, config);
                if (newStream && deviceId) {
                    const vt = newStream.getVideoTracks()[0];
                    this.currentCameraVideoDeviceId = vt ? vt.getSettings().deviceId : deviceId;
                    const at = newStream.getAudioTracks()[0];
                    this.currentCameraAudioDeviceId = at ? at.getSettings().deviceId : null;
                }
                break;
            case "microphone":
                config =this.getConfigByType(type, true, false, deviceId);
                newStream = await this.createMediaStreamByTypeAndConfig(type, config);
                break;
            case "movie":
                config =this.getConfigByType(type, true, true);
                const microphoneStream = await this.createMediaStreamByTypeAndConfig(type, config);
                const movieElement = document.getElementById('savedVideoElement');
                movieElement.oncanplay = () => movieElement.play();
                const movieoStream = movieElement.captureStream();

                newStream = new MediaStream();
                movieoStream.getVideoTracks().forEach(track => {newStream.addTrack(track);}); 
                microphoneStream.getAudioTracks().forEach(track => {newStream.addTrack(track);});
                break;
            case "screenshare":     
                try {
                    config=this.getConfigByType(type, audioOn, videoOn);
                    newStream = await this.createMediaStreamByTypeAndConfig(type, config);
                } catch(e) {
                    throw (e instanceof MakedoError) ? e : fromMediaError(e, 'screenshare', 'startMedia');
                }
                break;
        }
        //if (newStream) { this.streamsLocal[type] = newStream; }
        this.local_setStream(type, newStream);
        return newStream;
    }
    getMedia(type) {
        let stream=this.sourceStreams[type];
        return stream;
    }
    local_setTracksState(type, audioOn, videoOn) { //→ void
    }
    local_killStream(type) { //→ void
        // A tap cannot outlive its source stream being killed outright (room exit,
        // stopLive(keepMedia=false)). Note this is NOT the camera-off path — exitVideoTrack()
        // only stops the track, and taps deliberately survive that, going black instead of
        // disappearing from the remote grid.
        this.releaseVideoTap(type);        // `type` was itself a tap
        this.endVideoTapsOfSource(type);   // `type` is a source other labels tap from
        let stream = this.sourceStreams[type];
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            delete this.sourceStreams[type];
        }
    }
    
    // Kill only video track for a stream type (keeps audio alive)
    exitVideoTrack(type) {
        // Stop video in source stream
        let sourceStream = this.sourceStreams[type];
        if (sourceStream) {
            sourceStream.getVideoTracks().forEach(track => {
                track.stop();
                sourceStream.removeTrack(track);
            });
        }
        
        // Stop video in cloned upstream
        let upstream = this.activeUpstreams[type];
        if (upstream && upstream.clonedStream) {
            upstream.clonedStream.getVideoTracks().forEach(track => {
                track.stop();
                upstream.clonedStream.removeTrack(track);
            });
        }
        
        // Clear video track reference in unified registry
        if (this.activeUpstreams[type]) this.activeUpstreams[type].videoTrack = null;

        // Derived taps deliberately SURVIVE camera-off: they go black rather than vanishing
        // from remote grids, and resume when the camera returns. Re-point explicitly rather
        // than waiting on the track's async 'ended' event, so the transition is deterministic.
        this.retargetVideoPipeline(type);
    }

    // Kill only audio track for a stream type (keeps video alive)
    exitAudioTrack(type) {
        // Stop audio in source stream
        let sourceStream = this.sourceStreams[type];
        if (sourceStream) {
            sourceStream.getAudioTracks().forEach(track => {
                track.stop();
                sourceStream.removeTrack(track);
            });
        }
        
        // Stop audio in cloned upstream
        let upstream = this.activeUpstreams[type];
        if (upstream && upstream.clonedStream) {
            upstream.clonedStream.getAudioTracks().forEach(track => {
                track.stop();
                upstream.clonedStream.removeTrack(track);
            });
        }
        
        // Clear audio track reference in unified registry
        if (this.activeUpstreams[type]) this.activeUpstreams[type].audioTrack = null;
    }
    
    // Track-level replacement functions for independent audio/video management
    async local_replaceVideoTrack(type, deviceId) {
        let stream = this.sourceStreams[type];
        if (!stream) return;
        
        // Stop and remove old video track
        let oldVideoTrack = stream.getVideoTracks()[0];
        if (oldVideoTrack) {
            oldVideoTrack.stop();
            stream.removeTrack(oldVideoTrack);
        }
        
        // Get new video track
        let config = this.getConfigByType(type, false, true, deviceId);
        
        try {
            let videoStream = await this.createMediaStreamByTypeAndConfig(type, config);
            let newVideoTrack = videoStream.getVideoTracks()[0];
            stream.addTrack(newVideoTrack);

            // Sync to clone if exists
            this.syncVideoTrackToClone(type, newVideoTrack);
            // Feed the new device to any derived taps. Their own published tracks are unaffected,
            // so a camera switch never interrupts a thumbnail.
            this.retargetVideoPipeline(type);
        } catch(e) {
            throw (e instanceof MakedoError) ? e : fromMediaError(e, type, 'local_replaceVideoTrack');
        }
    }
    
    async local_replaceAudioTrack(type, deviceId) {
        let stream = this.sourceStreams[type];
        if (!stream) return;
        
        // Stop and remove old audio track
        let oldAudioTrack = stream.getAudioTracks()[0];
        if (oldAudioTrack) {
            oldAudioTrack.stop();
            stream.removeTrack(oldAudioTrack);
        }
        
        // Get new audio track
        let config = this.getConfigByType(type, true, false, deviceId); 
        
        try {
            let audioStream = await this.createMediaStreamByTypeAndConfig(type, config);
            let newAudioTrack = audioStream.getAudioTracks()[0];
            stream.addTrack(newAudioTrack);
            
            // Sync to clone if exists
            this.syncAudioTrackToClone(type, newAudioTrack);
        } catch(e) {
            throw (e instanceof MakedoError) ? e : fromMediaError(e, type, 'local_replaceAudioTrack');
        }
    }
    
    async local_addAudioTrack(type, deviceId) {
        let stream = this.sourceStreams[type];
        if (!stream) return;
        
        let config = this.getConfigByType(type, true, false, deviceId); 
        
        try {
            let audioStream = await this.createMediaStreamByTypeAndConfig(type, config);
            let newAudioTrack = audioStream.getAudioTracks()[0];
            stream.addTrack(newAudioTrack);
            
            // Sync to clone if exists
            this.syncAudioTrackToClone(type, newAudioTrack);
        } catch(e) {
            throw (e instanceof MakedoError) ? e : fromMediaError(e, type, 'local_addAudioTrack');
        }
    }
    
    async local_addVideoTrack(type, deviceId) {
        let stream = this.sourceStreams[type];
        if (!stream) return;
        
        let config = this.getConfigByType(type, false, true, deviceId); 
        
        try {
            let videoStream = await this.createMediaStreamByTypeAndConfig(type, config);
            let newVideoTrack = videoStream.getVideoTracks()[0];
            stream.addTrack(newVideoTrack);

            // Sync to clone if exists
            this.syncVideoTrackToClone(type, newVideoTrack);
            // Camera coming back on after setVideo(false): re-point taps, which have been
            // publishing black in the meantime rather than disappearing from the grid.
            this.retargetVideoPipeline(type);
        } catch(e) {
            throw (e instanceof MakedoError) ? e : fromMediaError(e, type, 'local_addVideoTrack');
        }
    }
    
    // Sync functions to keep source and clone tracks in sync
    async syncVideoTrackToClone(type, newVideoTrack) {
        let upstream = this.activeUpstreams[type];
        if (!upstream) return; // Not broadcasting yet
        
        let clonedStream = upstream.clonedStream;
        let gStream = upstream.gStream;
        
        // Remove old video track from clone
        let oldClonedVideoTrack = clonedStream.getVideoTracks()[0];
        if (oldClonedVideoTrack) {
            clonedStream.removeTrack(oldClonedVideoTrack);
            oldClonedVideoTrack.stop();
        }
        
        // Clone the new track and add to cloned stream
        let newClonedVideoTrack = newVideoTrack.clone();
        clonedStream.addTrack(newClonedVideoTrack);
        
        // Update track reference in unified registry
        this.activeUpstreams[type].videoTrack = newClonedVideoTrack;
        
        // Add or replace track in peer connection
        if (gStream && gStream.pc) {
            let sender = gStream.pc.getSenders().find(s => s.track && s.track.kind === 'video');
            if (sender) {
                // Replace existing video track
                sender.replaceTrack(newClonedVideoTrack);

                // ADDED FEB 22 TO TRY TO IMPROVE QUALITY ON NEW VIDEO TRACK
                const params = sender.getParameters();
                if (params.encodings && params.encodings.length > 0) {
                    params.encodings[0].minBitrate = 500000;   // 500 Kbps floor
                    params.encodings[0].maxBitrate = 4000000;
                    params.encodings[0].priority = 'high';
                    params.encodings[0].networkPriority = 'high';
                }
                await sender.setParameters(params);

            } else {
                // No video sender exists - add new transceiver
                let encodings = this.getEncodingsByTypeKind(type, 'video');
                let transceiver = gStream.pc.addTransceiver(newClonedVideoTrack, {
                    direction: 'sendonly',
                    streams: [clonedStream],
                    sendEncodings: encodings
                });
                this.preferVP9(transceiver);
            }
        }
    }
    
    syncAudioTrackToClone(type, newAudioTrack) {
        let upstream = this.activeUpstreams[type];
        if (!upstream) return; // Not broadcasting yet
        
        let clonedStream = upstream.clonedStream;
        let gStream = upstream.gStream;
        
        // Remove old audio track from clone
        let oldClonedAudioTrack = clonedStream.getAudioTracks()[0];
        if (oldClonedAudioTrack) {
            clonedStream.removeTrack(oldClonedAudioTrack);
            oldClonedAudioTrack.stop();
        }
        
        // Clone the new track and add to cloned stream
        let newClonedAudioTrack = newAudioTrack.clone();
        clonedStream.addTrack(newClonedAudioTrack);
        
        // Update track reference in unified registry
        this.activeUpstreams[type].audioTrack = newClonedAudioTrack;
        
        // Add or replace track in peer connection
        if (gStream && gStream.pc) {
            let sender = gStream.pc.getSenders().find(s => s.track && s.track.kind === 'audio');
            if (sender) {
                // Replace existing audio track
                sender.replaceTrack(newClonedAudioTrack);
            } else {
                // No audio sender exists - add new transceiver
                let encodings = this.getEncodingsByTypeKind(type, 'audio');
                gStream.pc.addTransceiver(newClonedAudioTrack, {
                    direction: 'sendonly',
                    streams: [clonedStream],
                    sendEncodings: encodings
                });
            }
        }
    }
    
    syncVideoToClone(type, enabled) {
        let upstream = this.activeUpstreams[type];
        if (!upstream) return;
        
        let clonedVideoTrack = upstream.clonedStream.getVideoTracks()[0];
        if (clonedVideoTrack) {
            clonedVideoTrack.enabled = enabled;
        }
    }
    
    syncAudioToClone(type, enabled) {
        let upstream = this.activeUpstreams[type];
        if (!upstream) return;
        
        let clonedAudioTrack = upstream.clonedStream.getAudioTracks()[0];
        if (clonedAudioTrack) {
            clonedAudioTrack.enabled = enabled;
        }
    }
    
async addAudioTrackToExistingCameraGStream(gStream, deviceId, replaceExistingInPeerConnection) { // PRIVATE function
        let type="camera";
        let currentStream=gStream.stream;
        let peerConnection=gStream.pc;
        let newAudioConfig=this.getConfigByType(type, true, false, deviceId);
        // Get new audio stream
        const newAudioStream = await this.createMediaStreamByTypeAndConfig(type, newAudioConfig);
        // Get the new audio track
        const newAudioTrack = newAudioStream.getAudioTracks()[0];
        // Stop and remove old audio track
        const oldAudioTrack = currentStream.getAudioTracks()[0];
        if (oldAudioTrack) {
            oldAudioTrack.stop();
            currentStream.removeTrack(oldAudioTrack);
        }
        // Add new audio track to existing stream
        currentStream.addTrack(newAudioTrack);
        if (replaceExistingInPeerConnection) {
            // Find the audio sender
            const audioSender = peerConnection.getSenders().find(sender => sender.track && sender.track.kind === 'audio');

            if (audioSender) {
                const oldTrack = audioSender.track;
 
                await audioSender.replaceTrack(newAudioTrack); 
                if (oldTrack) { oldTrack.stop(); }
            }    
        } else {
            // Add the new track to the peer connection
            const sender = peerConnection.addTrack(newAudioTrack, currentStream);
        }
        if (this.activeUpstreams["camera"]) this.activeUpstreams["camera"].audioTrack = newAudioTrack;
        // Clean up the temporary stream
        newAudioStream.getAudioTracks().forEach(track => {
            if (track !== newAudioTrack) track.stop();
        });
    }
    async addVideoTrackToExistingCameraGStream(gStream, deviceId, replaceExistingInPeerConnection) { // PRIVATE function
        let type="camera";
        let currentStream=gStream.stream;
        let peerConnection=gStream.pc;
        let newVideoConfig=this.getConfigByType(type, false, true, deviceId);
        // Get new video stream
        const newVideoStream = await this.createMediaStreamByTypeAndConfig(type, newVideoConfig);
        // Get the new video track
        const newVideoTrack = newVideoStream.getVideoTracks()[0];
        // Stop and remove old video track
        const oldVideoTrack = currentStream.getVideoTracks()[0];
        if (oldVideoTrack) {
            oldVideoTrack.stop();
            currentStream.removeTrack(oldVideoTrack);
        }
        // Add new video track to existing stream
        currentStream.addTrack(newVideoTrack);
        if (replaceExistingInPeerConnection) {
            // Find the video sender
            const videoSender = peerConnection.getSenders().find(sender => sender.track && sender.track.kind === 'video');

            if (videoSender) {
                const oldTrack = videoSender.track;
 
                await videoSender.replaceTrack(newVideoTrack); 

                // ADDED FEB 22 TO TRY TO IMPROVE QUALITY ON NEW VIDEO TRACK
                const params = videoSender.getParameters();
                if (params.encodings && params.encodings.length > 0) {
                    params.encodings[0].minBitrate = 500000;   // 500 Kbps floor
                    params.encodings[0].maxBitrate = 4000000;
                    params.encodings[0].priority = 'high';
                    params.encodings[0].networkPriority = 'high';
                }
                await videoSender.setParameters(params);

                if (oldTrack) { oldTrack.stop(); }
            }    
        } else {
            // Add the new track to the peer connection
            const sender = peerConnection.addTrack(newVideoTrack, currentStream);
        }
        if (this.activeUpstreams["camera"]) this.activeUpstreams["camera"].videoTrack = newVideoTrack;
        // Clean up the temporary stream
        newVideoStream.getVideoTracks().forEach(track => {
            if (track !== newVideoTrack) track.stop();
        });
    }


    // FUNCTIONS FOR MANAGING REMOTE SERVER CONNECTIONS
     setupServer(channelId, sfu_url, memberPubId, accessCode) { // CALLED BY ChannelComms CLASS
        let newChannel={};
        newChannel.sfu_url=sfu_url;
        newChannel.channelId=channelId;
        newChannel.joinAsUsername=memberPubId;
        newChannel.joinWithPassword=accessCode;
        this.currentChannel=newChannel;
        this.bridgeSetMemberStatusDb(memberPubId, "lobby_pre", "member_in_lobby"); // set to "lobby_pre" when new channel is set 
    }
    async  joinServer() { // CALLED BY ChannelComms CLASS
        let targetChannel=this.currentChannel;
        try {
            this.bridgeSetLatestChannelDb(targetChannel.channelId);
            await this.loginToCurrentChannel();
        } catch(e) {
            throw (e instanceof MakedoError) ? e
                : new MakedoError(ErrorCodes.ROOM_SFU_UNREACHABLE, e.message || 'Failed to connect to video server', { cause: e, context: 'joinServer', retriable: true });
        };
    }
     leaveServer(status = "steppedout") { // Called by ChannelComms CLASS
        if (!this.sfuConnection) {return;}
        this.weInitiatedLeave = true;  // Mark that WE initiated this disconnect
        this.bridgeSetMemberStatusDb(this.sfuConnection.username, status, "member_left"); 
        console.debug("Member status set to " + status); 
        this.sfuConnection.close();
    }
    getMyUpstream(type) {
        let gStream= this.getGUpstreamByTypeFromConnection(type);
        if (gStream && gStream.stream) { return gStream.stream; }
        return null;
    }

    /**
     * Enumerate all active peer connections (upstream + downstream) currently held by
     * the Galene client. Used by the optional connection-quality module to sample
     * RTCPeerConnection.getStats() each tick. Returns an empty array when not connected.
     * @returns {Array<{direction:'up'|'down', memberId:string, streamType:string, pc:RTCPeerConnection}>}
     */
    getAllActivePeerConnections() {
        const out = [];
        const conn = this.sfuConnection;
        if (!conn) return out;
        const collect = (bag, direction) => {
            if (!bag) return;
            for (const id in bag) {
                const g = bag[id];
                if (g && g.pc) {
                    out.push({ direction, memberId: g.username, streamType: g.label, pc: g.pc });
                }
            }
        };
        collect(conn.up, 'up');
        collect(conn.down, 'down');
        return out;
    }

    /**
     * Find the specific type media stream, if any.
     *
     * @parm {string} conn
     * @returns {gStream}
     */
     getGUpstreamByTypeFromConnection(mediaSourceType) { // PRIVATE function
        let conn=this.sfuConnection;
        if (!conn) {return null;}
        for(let id in conn.up) {
            let gStream = conn.up[id];
            if(gStream.label === mediaSourceType) {return gStream;}
        }
        return null;
    }
    killAllUpStreamsByTypeFromConnection(mediaSourceType) { // PRIVATE function, never used?
        let conn=this.sfuConnection;
        if (!conn) {return null;}
        for(let id in conn.up) {
            let gStream = conn.up[id];
            if(gStream.label === mediaSourceType) {this.stopEntireGUpstream(gStream);}
        }
    }


    broadcastLocalStream(type) { // Called by ChannelComms CLASS
        let existingStream=this.getMedia(type);
        if (!existingStream) {console.error("broadcastLocalStream...streams.ERROR: No stream to broadcast for type: "+type); return;}
        this.addGUpstreamBySourceTypePlus(type);
    }

    // ============================  DERIVED (TAPPED) VIDEO  ============================
    // A VideoPipeline reads ONE source label's video track and fans it out to N downscaled
    // taps. The source label keeps publishing its own NATIVE track untouched — adding or
    // removing a tap never calls replaceTrack and never renegotiates, so publishing a
    // thumbnail mid-call cannot disturb the camera stream it is derived from.

    /** Create (or extend) the pipeline on `sourceLabel` and return a MediaStream for one tap. */
    tapVideoFromLabel(sourceLabel, tapLabel, { width, height, fps, fit } = {}) {
        let sourceStream = this.sourceStreams[sourceLabel];
        let sourceTrack = sourceStream ? sourceStream.getVideoTracks().find(t => t.readyState === 'live') : null;
        if (!sourceTrack) {
            throw new MakedoError(ErrorCodes.PIPELINE_SOURCE_UNAVAILABLE, `publishStream: source label "${sourceLabel}" has no live video track to derive "${tapLabel}" from`, { context: 'publishStream', retriable: false });
        }
        let pipe = this.videoPipelines[sourceLabel];
        let created = false;
        if (!pipe) {
            pipe = new VideoPipeline({
                sourceLabel,
                onStats: (s) => { this.videoPipelineStats[sourceLabel] = s; },
                onError: (e) => { console.warn(`Streams: video pipeline "${sourceLabel}":`, e); },
            });
            this.videoPipelines[sourceLabel] = pipe;
            created = true;
        }
        pipe.setInputTrack(sourceTrack);
        let tapTrack;
        try {
            tapTrack = pipe.addOutput(tapLabel, { width, height, fps, fit });
        } catch (e) {
            // Don't leave an output-less pipeline behind when the tap spec was rejected.
            if (created) { pipe.destroy(); delete this.videoPipelines[sourceLabel]; }
            throw e;
        }
        this.videoTaps[tapLabel] = sourceLabel;
        return new MediaStream([tapTrack]);
    }

    /** Re-point a pipeline at whatever video track its source label currently holds. */
    retargetVideoPipeline(sourceLabel) {
        let pipe = this.videoPipelines[sourceLabel];
        if (!pipe) { return; }
        let stream = this.sourceStreams[sourceLabel];
        let track = stream ? stream.getVideoTracks().find(t => t.readyState === 'live') : null;
        pipe.setInputTrack(track || null); // null → taps stay alive and paint black
    }

    /** Drop one tap. Destroys the pipeline once its last tap is gone. */
    releaseVideoTap(tapLabel) {
        let sourceLabel = this.videoTaps[tapLabel];
        if (!sourceLabel) { return; }
        delete this.videoTaps[tapLabel];
        let pipe = this.videoPipelines[sourceLabel];
        if (!pipe) { return; }
        pipe.removeOutput(tapLabel);
        if (!pipe.hasOutputs()) {
            pipe.destroy();
            delete this.videoPipelines[sourceLabel];
            delete this.videoPipelineStats[sourceLabel];
        }
    }

    /** Tear down every tap derived from `sourceLabel`, including their upstreams. */
    endVideoTapsOfSource(sourceLabel) {
        let pipe = this.videoPipelines[sourceLabel];
        if (!pipe) { return; }
        // Delete the registry entries BEFORE recursing: endFullStreamByType() below re-enters
        // local_killStream() for each tap, which calls back into releaseVideoTap()/
        // endVideoTapsOfSource(). Clearing first is what terminates that recursion.
        delete this.videoPipelines[sourceLabel];
        delete this.videoPipelineStats[sourceLabel];
        let tapLabels = pipe.outputNames();
        pipe.destroy();
        tapLabels.forEach(tapLabel => {
            delete this.videoTaps[tapLabel];
            delete this.customEncodings[tapLabel];
            this.endFullStreamByType(tapLabel);
        });
    }

    /** Last measured pipeline stats for a source label (or all of them). Diagnostic. */
    getVideoPipelineStats(sourceLabel) {
        return sourceLabel ? (this.videoPipelineStats[sourceLabel] || null) : this.videoPipelineStats;
    }

    // ============================  CUSTOM LABELED UPSTREAMS  ============================
    // Publish an arbitrary-label upstream (e.g. a low-bandwidth thumbnail, a processed
    // canvas stream, or a second camera). Reserved labels are rejected — they have their
    // own named APIs. Each simultaneous upstream needs a UNIQUE label: Galene keys transport
    // by random id but derives per-user stream views by label (recomputeUserStreams), so a
    // duplicate label collapses. Backward compatibility: this path never touches the camera
    // capture recipe, the 4 Mbps cap, or the 500 kbps floor — those remain camera-only.
    // opts — exactly ONE source form:
    //   stream?: MediaStream                     app-supplied (e.g. a compositor canvas)
    //   constraints?: MediaStreamConstraints     capture a fresh device stream
    //   from?: string                            DERIVE from another label's video (see below)
    // plus:
    //   width?, height?, fps?, fit?              tap geometry — `from` only
    //   encodings?: RTCRtpEncodingParameters[]
    //   contentHint?: 'motion'|'detail'|'text'|'' (default 'motion' — favors frame rate)
    //
    // `from` taps the named label's video, downscales it and publishes the result — one read,
    // N outputs, so a 64x64 mosaic tile derived from the camera costs a blit rather than a
    // second capture. The source label keeps publishing its own native track untouched. When a
    // video processor is installed on the source (a later SDK version), the tap carries the
    // PROCESSED image; the contract here does not change.
    async publishStream(label, opts = {}) { // Called by ChannelComms CLASS
        if (!label || typeof label !== 'string') {
            throw new MakedoError(ErrorCodes.INVALID_PARAM, 'publishStream: a string label is required', { context: 'publishStream', retriable: false });
        }
        if (RESERVED_LABELS.includes(label)) {
            throw new MakedoError(ErrorCodes.INVALID_PARAM, `publishStream: "${label}" is a reserved label — use the camera/screenshare APIs instead`, { context: 'publishStream', retriable: false });
        }
        if (this.activeUpstreams[label] || this.getGUpstreamByTypeFromConnection(label)) {
            throw new MakedoError(ErrorCodes.DUPLICATE_STREAM_LABEL, `publishStream: label "${label}" is already published`, { context: 'publishStream', retriable: false });
        }
        let { stream, constraints, from, width, height, fps, fit, encodings, contentHint } = opts;
        if ([stream, constraints, from].filter(Boolean).length > 1) {
            throw new MakedoError(ErrorCodes.INVALID_PARAM, 'publishStream: give exactly one source — stream, constraints, or from', { context: 'publishStream', retriable: false });
        }
        if (!stream && from) {
            if (typeof from !== 'string') {
                throw new MakedoError(ErrorCodes.INVALID_PARAM, 'publishStream: "from" must be the label of an existing stream', { context: 'publishStream', retriable: false });
            }
            if (!width || !height) {
                throw new MakedoError(ErrorCodes.INVALID_PARAM, 'publishStream: "from" requires an explicit width and height for the derived stream', { context: 'publishStream', retriable: false });
            }
            stream = this.tapVideoFromLabel(from, label, { width, height, fps, fit });
        }
        if (!stream) {
            if (!constraints) {
                throw new MakedoError(ErrorCodes.INVALID_PARAM, 'publishStream: provide a MediaStream (stream), capture constraints (constraints), or a source label (from)', { context: 'publishStream', retriable: false });
            }
            try {
                stream = await navigator.mediaDevices.getUserMedia(constraints);
            } catch (e) {
                throw (e instanceof MakedoError) ? e : fromMediaError(e, label, 'publishStream');
            }
        }
        // Bias the encoder. Default 'motion' (favor frame rate over spatial detail) matches the
        // camera capture default and avoids the footgun where an unhinted canvas/low-motion
        // source is encoded at a very low frame rate. The source track is the one added to the
        // peer connection, so setting contentHint here reaches the encoder. Pass contentHint:
        // 'detail' for text/screen-like content, or '' to leave the track's hint unset.
        const hint = ('contentHint' in opts) ? contentHint : 'motion';
        if (hint) {
            stream.getVideoTracks().forEach(t => { if ('contentHint' in t) { t.contentHint = hint; } });
        }
        if (encodings) {
            // App-supplied encodings apply to the video sender; keep a sane audio default.
            this.customEncodings[label] = { video: encodings, audio: [{ maxBitrate: 128000 }] };
        }
        this.local_setStream(label, stream);
        this.addGUpstreamBySourceTypePlus(label); // no-op if not yet connected to the SFU
        return stream;
    }

    unpublishStream(label) { // Called by ChannelComms CLASS
        if (!label || RESERVED_LABELS.includes(label)) { return; } // reserved labels tear down via their own paths
        this.endFullStreamByType(label); // generic: kills source + gStream + activeUpstreams entry
        delete this.customEncodings[label];
    }

    // ============================  DOWNSTREAM SUBSCRIPTION  ============================
    // Locate a specific incoming downstream by (member, label). Galene sets gStream.username
    // to the member PID and gStream.label to the stream label at offer time.
    getGDownstreamByMemberAndLabel(memberId, label) { // PRIVATE function
        let conn = this.sfuConnection;
        if (!conn) { return null; }
        for (let id in conn.down) {
            let gStream = conn.down[id];
            if (gStream.username === memberId && gStream.label === label) { return gStream; }
        }
        return null;
    }

    // Request a specific track set for ONE incoming stream. kinds: ['audio','video'] | ['video-low'] | ['audio'] | []
    // ([] = receive nothing for this stream, freeing its bandwidth without closing it). Returns false when the
    // stream is not present locally (e.g. not offered under the current default subscription).
    subscribeToStream(memberId, label, kinds) { // Called by ChannelComms CLASS
        let gStream = this.getGDownstreamByMemberAndLabel(memberId, label);
        if (gStream) {
            // Per-stream request on an OPEN downstream:
            //   null/undefined -> revert this stream to the connection default (Galene semantics)
            //   []             -> request zero tracks: Galene CLOSES the downstream (fires remoteStreamEnd)
            //   ['video'] | ['video-low'] | ['audio'] -> select tracks/layer on the open stream
            gStream.request((kinds === undefined || kinds === null) ? null : kinds);
            return true;
        }
        // No open downstream for (member,label) — e.g. a prior [] request tore it down. Per-stream
        // requests cannot reopen a closed stream; reopening is a connection-level re-offer (see
        // setDefaultSubscription). Return false so the caller knows this was not a live change.
        return false;
    }
    unsubscribeFromStream(memberId, label) { // Called by ChannelComms CLASS
        let gStream = this.getGDownstreamByMemberAndLabel(memberId, label);
        if (gStream) { gStream.request([]); return true; }
        return false;
    }
    // Set the default subscription map applied on join/change. '' is the wildcard for unlisted labels.
    setDefaultSubscription(map) { // Called by ChannelComms CLASS
        this.defaultRequest = map || { '': ['audio', 'video'] };
        if (this.sfuConnection) { this.sfuConnection.request(this.defaultRequest); }
    }
    // Discovery: enumerate the labels this member is currently publishing that we have received.
    getMemberStreamLabels(memberId) { // Called by ChannelComms CLASS
        let out = [];
        let conn = this.sfuConnection;
        if (!conn) { return out; }
        for (let id in conn.down) {
            let gStream = conn.down[id];
            if (gStream.username === memberId && gStream.label && !out.includes(gStream.label)) {
                out.push(gStream.label);
            }
        }
        return out;
    }

    
    setStreamAudioTrack(type, audioOn, deviceId) { // Called by ChannelComms CLASS
    //setCameraAudio(audioOn,deviceId) { // Called by ChannelComms CLASS, confirms audio device ON as needed (if onOff  true)
        if (!this.sfuConnection.username) {console.warn('WEIRD: Setting camera audio: but no member-name'); return;}
        this.bridgeSetMemberMedia(this.sfuConnection.username, type, audioOn, null);

        let gStream= this.getGUpstreamByTypeFromConnection(type);
       let stream=this.getMyUpstream(type);
       let alreadyHasStream=(stream != null);
       let alreadyHasAudio=(alreadyHasStream && stream.getAudioTracks().length > 0);
        if (audioOn===false) {
 
            if (!alreadyHasAudio) {return;}
            else {this.toggleUpAudioTrack(type, false); return;}
        }
        if (audioOn===true) {
            if (alreadyHasAudio) { 
                if (deviceId==this.currentCameraAudioDeviceId) {

                    this.toggleUpAudioTrack(type, true);
                } else {
                    this.addAudioTrackToExistingCameraGStream(gStream, deviceId, true); // replace existing track on peer connection
                }
            } else if (alreadyHasStream) {
                this.addAudioTrackToExistingCameraGStream(gStream, deviceId, false);
            } else {
                this.startMedia(type, true, false, deviceId);
                this.addGUpstreamBySourceTypePlus(type);
                this.toggleUpAudioTrack(type, true);
            }
            this.currentCameraAudioDeviceId=deviceId;
            return;
        }
    } 
    setStreamVideoTrack(type, videoOn, deviceId) { // Called by ChannelComms CLASS
    //setCameraVideo(videoOn,deviceId) { // Called by ChannelComms CLASS, confirms video device on as needed (if onOff  true)
        let existingStream=this.getMedia(type);
        if (!this.sfuConnection.username) {console.warn('WEIRD: Setting camera video: but no member-name'); return;}
        this.bridgeSetMemberMedia(this.sfuConnection.username, type, null, videoOn);
        
        let gStream= this.getGUpstreamByTypeFromConnection(type);
       let stream=this.getMyUpstream(type);
       let alreadyHasStream=(stream != null);
       let alreadyHasVideo=(alreadyHasStream && stream.getVideoTracks().length > 0);
        if (videoOn===false) {

            if (!alreadyHasVideo) { return;}
            else {this.toggleUpVideoTrack(type, false); return;}
        }
        if (videoOn===true) {
            if (alreadyHasVideo) {
                if (deviceId==this.currentCameraVideoDeviceId) {
                   this.toggleUpVideoTrack(type, true);
                } else {
                    this.addVideoTrackToExistingCameraGStream(gStream, deviceId, true); // replace existing track on peer connection
                }
            } else if (alreadyHasStream) {
                this.addVideoTrackToExistingCameraGStream(gStream, deviceId, false);
            } else {
                this.startMedia(type, false, true, deviceId);
                this.addGUpstreamBySourceTypePlus(type);  // DOES THIS REMOVE OLD TRACK_LESS STREAM? NO!
                this.toggleUpVideoTrack(type, true);
            }
            this.currentCameraVideoDeviceId=deviceId;
            return;
        }
    } 
    //setCameraBoth(tracksOn,deviceId) { // Called by ChannelComms CLASS, confirms both audio and video ON as needed (if onOff  true)
    setStreamBothTracks(type, tracksOn,deviceId) { // Called by ChannelComms CLASS, confirms both audio and video ON as needed (if onOff  true)
        if (!this.sfuConnection.username) {console.warn('WEIRD: Setting camera both: but no member-name'); return;}
        this.bridgeSetMemberMedia(this.sfuConnection.username, type, tracksOn, tracksOn);
       let stream=this.getMyUpstream(type);
       let alreadyHasStream=(stream != null);
       let alreadyHasVideo=(alreadyHasStream && stream.getVideoTracks().length > 0);
       let alreadyHasAudio=(alreadyHasStream && stream.getAudioTracks().length > 0);
        if (tracksOn===false) {
            if (!alreadyHasAudio && !alreadyHasVideo) {
                return;
            } else {
                if (alreadyHasAudio) {this.toggleUpAudioTrack(type, false);}
                if (alreadyHasVideo) {this.toggleUpVideoTrack(type, false);}
                return;
            }
        }
        if (tracksOn===true) {
            if (alreadyHasAudio && alreadyHasVideo) {
                this.toggleUpAudioTrack(type, true); 
                this.toggleUpVideoTrack(type, true); 
            } else {
                this.startMedia(type, true, true, deviceId);
                this.addGUpstreamBySourceTypePlus(type);
                this.toggleUpAudioTrack(type, true);
                this.toggleUpVideoTrack(type, true);
            }
            this.currentCameraAudioDeviceId=deviceId;
            this.currentCameraVideoDeviceId=deviceId;
        }
    } 
    endFullStreamByType(type) { // Called by ChannelComms CLASS
        // First kill the source stream
        this.local_killStream(type);
        // Then stop the gStream being sent to server
        let gStream = this.getGUpstreamByTypeFromConnection(type);
        if (gStream) {
            this.stopEntireGUpstream(gStream);
        }
        // Clear unified upstream registry entry
        delete this.activeUpstreams[type];
        return;
    }
    closeUpstreamOnly(type) { // Called by ChannelComms CLASS during PAUSE
        // Close the upstream gStream but keep source stream alive
        let gStream = this.getGUpstreamByTypeFromConnection(type);
        if (gStream) {
            this.stopEntireGUpstream(gStream);
            // Clear unified upstream registry entry
            delete this.activeUpstreams[type];
        }
        return;
    }
    async stopGStreamTracks(gStream, stopAudio, stopVideo) {
        if (!gStream?.stream) return;
        let stream = gStream.stream;
        if (stopAudio) {stream.getAudioTracks().forEach(track => {track.stop(); stream.removeTrack(track);});}
        if (stopVideo) {stream.getVideoTracks().forEach(track => {track.stop(); stream.removeTrack(track);});}
    }

    toggleStreamByType(type, forceVal, deviceId) { // Called by ChannelComms CLASS
        if (!type) {return;}
        if (!["camera", "microphone", "movie", "screenshare"].includes(type)) {return;}
        let useForceVal= (typeof forceVal === 'boolean');
        let streamState=false;
        let gStream = this.getGUpstreamByTypeFromConnection(type); 
        if (useForceVal) {
            if (forceVal) {
              this.startMedia(type, true, true, deviceId); 
              this.addGUpstreamBySourceTypePlus(type); 
              streamState=true;
            } else {if (gStream) {this.stopEntireGUpstream(gStream);} streamState=false;}
        } else {
            if(!gStream) { 
                this.startMedia(type, true, true, deviceId);
                this.addGUpstreamBySourceTypePlus(type); 
                streamState=true;
            } else { this.stopEntireGUpstream(gStream); streamState=false;}
        }
        return streamState;
    }

    getAndSetNewGUpstream(mediaSourceType, fullStream) { // PRIVATE function
        let conn=this.sfuConnection;
        if (!conn) {return null;}
        let gStream = conn.newUpStream();
        gStream.username = conn.username;
        gStream.label = mediaSourceType;
        
        // Clone the stream so Galene's peer connection doesn't stop our source tracks
        // This allows local preview to continue even when paused/disconnected
        let clonedStream = fullStream.clone();
        gStream.setStream(clonedStream);
        
        // Track this active upstream for lifecycle management
        this.activeUpstreams[mediaSourceType] = {
            gStream: gStream,
            clonedStream: clonedStream,
            audioTrack: null,  // set after tracks are wired in addGUpstreamBySourceTypePlus
            videoTrack: null,  // set after tracks are wired in addGUpstreamBySourceTypePlus
        };
        
        return gStream;
    }

    /**
     * Enable and broadcast some stream to the channel.
     *
     * @parm {ServerConnection} conn
     */
    async addGUpstreamBySourceTypePlus(mediaSourceType) { // Private function, USED
        if (!this.sfuConnection) {return null;}

        let fullStream=null, gStream=null;

        // Any non-empty label is broadcastable — reserved OR custom. The source stream must
        // already exist (reserved: startMedia; custom: publishStream via local_setStream).
        if (!mediaSourceType) { throw new Error("addGUpstreamBySourceTypePlus: missing stream label"); }
        let existingStream = this.getMedia(mediaSourceType);
        if (!existingStream) {return;}

        fullStream=existingStream;
        
        /* Send the new stream to the server, get gStream */
        gStream = this.getAndSetNewGUpstream(mediaSourceType, fullStream);

        // Track maps point to CLONED tracks (not original source tracks)
        // This ensures toggle operations affect what's sent to Galene
        let clonedStream = this.activeUpstreams[mediaSourceType].clonedStream;
        this.activeUpstreams[mediaSourceType].audioTrack = clonedStream.getAudioTracks()[0] || null;
        this.activeUpstreams[mediaSourceType].videoTrack = clonedStream.getVideoTracks()[0] || null;

        gStream.onclose = (replace) => {
            gStream.stream.getTracks().forEach(t => t.stop());
            this.onEndDownStreamExt(gStream.localId, gStream.username, gStream.label);
        };

        const addTrack = (t) => {  // arrow function — lexical 'this' refers to Streams instance
            t.onmute = (e) => {}; // <<<<<<<<<<<<<< SYSTEM-MUTE, ONLY UPSTREAM? FREQUENT FOR SCREENSHARE. WORKS?
            t.onunmute = (e) => {};  // <<<<<<<<<<<<<< SYSTEM-UNMUTE, ONLY UPSTREAM? FREQUENT FOR SCREENSHARE. WORKS?
            t.onended = (e) => {
                //fullStream.onaddtrack = null;
                gStream.onaddtrack = null;
                gStream.onremovetrack = null;
                gStream.close();
            };
            let encodings = this.getEncodingsByTypeKind(gStream.label, t.kind);
            let transceiver = gStream.pc.addTransceiver(t, {
                direction: 'sendonly',
                streams: [clonedStream],  // Send the CLONE, not the source!
                sendEncodings: encodings
            });
            if (t.kind === 'video') { this.preferVP9(transceiver); }
        };

        // Make sure all future tracks are added to the cloned stream
        clonedStream.onaddtrack = (e) => { addTrack(e.track); };
        // Add any existing tracks from source to peer connection
        fullStream.getTracks().forEach(addTrack);

        // Connect the MediaStream to the video element and start playing.
        this.onNewUpStreamExt(gStream.localId, gStream.username, gStream.label, fullStream);
    }

    
    toggleUpVideoTrack(streamSourceType, forceVal) { // Private function, USED
        if (!streamSourceType) {return}
        // Any label with an active upstream can be toggled; the null-checks below handle absence.
        
        // Toggle BOTH source and cloned tracks to keep them in sync
        let sourceStream = this.sourceStreams[streamSourceType];
        let videoTrack = this.activeUpstreams[streamSourceType]?.videoTrack; // Cloned track
        
        if (!videoTrack) {
            return;
        }
        
        let useForceVal = (typeof forceVal === 'boolean');
        let newVal = (useForceVal) ? forceVal : !videoTrack.enabled;
        
        // Update clone (what's sent to Galene)
        videoTrack.enabled = newVal;
        
        // Update source (what user sees locally) - keep in sync
        if (sourceStream) {
            let sourceVideoTrack = sourceStream.getVideoTracks()[0];
            if (sourceVideoTrack) {
                sourceVideoTrack.enabled = newVal;
            }
        }
        
        return videoTrack.enabled;
    }
    
    toggleUpAudioTrack(streamSourceType, forceVal) { // Private function, USED
        if (!streamSourceType) {return}
        // Any label with an active upstream can be toggled; the null-checks below handle absence.
        
        // Toggle BOTH source and cloned tracks to keep them in sync
        let sourceStream = this.sourceStreams[streamSourceType];
        let audioTrack = this.activeUpstreams[streamSourceType]?.audioTrack; // Cloned track
        
        if (!audioTrack) {
            return;
        }
        
        let useForceVal = (typeof forceVal === 'boolean');
        let newVal = (useForceVal) ? forceVal : !audioTrack.enabled;
        
        // Update clone (what's sent to Galene)
        audioTrack.enabled = newVal;
        
        // Update source (what user sees locally) - keep in sync
        if (sourceStream) {
            let sourceAudioTrack = sourceStream.getAudioTracks()[0];
            if (sourceAudioTrack) {
                sourceAudioTrack.enabled = newVal;
            }
        }
        
        return audioTrack.enabled;
    }
    async  setMutedForUpStream(streamType, trackType, isMuted) { // used by comms
        let isEnabled = !isMuted;  // muted=true means enabled=false
        if (!streamType) {return;}  // any label with an active upstream can be muted
        
        // Use unified upstream registry — these are the actual tracks in the peer connection
        if (trackType === 'audio') {
            let audioTrack = this.activeUpstreams[streamType]?.audioTrack;
            if (audioTrack) {
                audioTrack.enabled = isEnabled;
                
                // Also sync to source stream for local preview
                let sourceStream = this.sourceStreams[streamType];
                if (sourceStream) {
                    let sourceAudioTrack = sourceStream.getAudioTracks()[0];
                    if (sourceAudioTrack) sourceAudioTrack.enabled = isEnabled;
                }
            }
        } else if (trackType === 'video') {
            let videoTrack = this.activeUpstreams[streamType]?.videoTrack;
            if (videoTrack) {
                videoTrack.enabled = isEnabled;
                
                // Also sync to source stream for local preview
                let sourceStream = this.sourceStreams[streamType];
                if (sourceStream) {
                    let sourceVideoTrack = sourceStream.getVideoTracks()[0];
                    if (sourceVideoTrack) sourceVideoTrack.enabled = isEnabled;
                }
            }
        }
    }

    /**
     * Stop broadcasting.
     *
     * @parm {ServerConnection} conn
     * @parm {Stream} s
     */

    async  stopEntireGUpstream(gStream) { // Private function, USED
        let stream=gStream.stream;
        if (!stream) {
            gStream.close();
            return;
        }
        stream.getTracks().forEach(t => {
            t.stop();
            stream.removeTrack(t);
        });
        gStream.close();
    }
    
    async stopTracksByKindInUpStream(gStream, trackKind) { // Private function, never used?
        if (!gStream?.stream) return;
        let stream=gStream.stream;
        
        const getTracksFn = {
            'audio': () => stream.getAudioTracks(),
            'video': () => stream.getVideoTracks()
        }[trackKind] || (() => stream.getTracks());
        
        getTracksFn().forEach(track => {
            track.stop();
            stream.removeTrack(track);
        });
    }


     getStreamStates() { // Called by ChannelComms CLASS
        let stream = null;
        let cameraOn=false, movieOn=false, micStreamOn=false, screenshareOn=false, audioOn=false;
        stream = this.getMyUpstream("camera"); if (stream) {cameraOn=true;}
        stream = this.getMyUpstream("microphone"); if (stream) {micStreamOn=true;}
        stream = this.getMyUpstream("video"); if (stream) {movieOn=true;}
        stream = this.getMyUpstream("screenshare"); if (stream) {screenshareOn=true;}
        return {camera:cameraOn, movie:movieOn, screenshare:screenshareOn, microphone:micStreamOn};
    }


     getAllCameraAudioTracks() { // USED BY channel_comms_class.startRecordingAudio()  For transcribing!
        // Pure read: returns a FRESH array and never mutates the registry with the upstream
        // track. Non-live entries are dropped (and pruned) here so a missed teardown hook
        // degrades into a stale-but-filtered registry rather than a dead track in the mix.
        const tracks = [];
        for (const t of this.downstreamAudioTracks) {
            if (t.readyState === 'live') { tracks.push(t); }
            else { this.downstreamAudioTracks.delete(t); }
        }
        const upTrack = this.getUpCameraAudioTrack();
        if (upTrack) { tracks.push(upTrack); }
        return tracks;
     }
     getUpCameraAudioTrack() { // Private function, USED BY baudio.js  For transcribing!
        let stream = this.getMyUpstream("camera");
        if (stream) {
            let oneAudioTrack=stream.getAudioTracks()[0];
            if (oneAudioTrack) {
                return oneAudioTrack;
            }
        }
        return null;
     }

    // DB server / external communication functions (now using bridge)
    bridgeSetLatestChannelDb(channelId) { // Access to DB via bridge
        if (this.bridge) {
            this.bridge.setLatestChannelId_db(channelId);
        }
    }
    bridgeSetMemberStatusDb(memberPid, newStatus, delogAction) { // Access to DB via bridge
        if (this.bridge) {
            this.bridge.setMemberStatus(memberPid, newStatus);
            this.bridge.delog("member", memberPid, delogAction, {});
            this.bridge.DBPing("member", memberPid);
        }
    }
    bridgeSetMemberMedia(memberPid, type, audioOn, videoOn) { // Access to DB via bridge
        if (this.bridge) {
            this.bridge.setMemberMedia(memberPid, type, audioOn, videoOn);
        }
    }
    bridgeDisplayError(message) { // Access to UX via bridge
        console.error("utils.Error: "+message);
    }

    bridgeDisplayStatus(status) { // Access to UX via bridge
        console.debug("utils.Status: "+status);
    }

}

export default Streams;



